import { CalendarTodayOutlined } from '@mui/icons-material';
import { Box, Typography, useTheme } from '@mui/material';
import { type ReactNode } from 'react';

import { CopyButton } from '../../components/general/CopyButton';
import { LabelValue } from '../../components/general/LabelValue';
import { VivaStatusSwitchForVeranstaltung } from '../../components/general/VivaStatusSwitchForVeranstaltung.tsx';
import { geschaeftsbereiche, type Veranstaltung, type Zeitraeume } from '../../dtos';
import { formatDateRange, getVeranstaltungTrainers, printTerminart, printWithNoInfo, printWo } from '../../utils';
import { content } from './VeranstaltungDetailsHeader.content';

const renderZeitraum = (ablauf: Zeitraeume): ReactNode => {
  const startDate = ablauf.at(0)?.start;
  const endDate = ablauf.at(-1)?.end ?? null;

  if (!startDate) {
    return '-';
  }

  return formatDateRange(startDate, endDate);
};

export const VeranstaltungDetailsHeader = ({ veranstaltung }: { readonly veranstaltung: Veranstaltung }) => {
  const trainers = getVeranstaltungTrainers(veranstaltung.experteBlockungen);
  const theme = useTheme();

  return (
    <Box p={3} borderRadius={2} sx={{ background: theme.palette.surface.brandLight }}>
      <Box display="flex" gap={1} mb={3} height={32}>
        <CalendarTodayOutlined />
        <Typography variant="headingXs" sx={{ mr: 'auto' }}>
          {veranstaltung.titel}
        </Typography>
        <VivaStatusSwitchForVeranstaltung veranstaltung={veranstaltung} />
      </Box>
      <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" rowGap={1}>
        <LabelValue label={content.zeitraum} value={renderZeitraum(veranstaltung.ablauf)} />
        <LabelValue
          label={content.sapId}
          value={
            veranstaltung.veranstaltungSapId ? (
              <Box sx={{ '& button': { py: 0 }, display: 'flex', alignItems: 'center' }}>
                <Typography variant="labelLight">{veranstaltung.veranstaltungSapId}</Typography>
                <CopyButton text={veranstaltung.veranstaltungSapId.toString()} />
              </Box>
            ) : (
              '-'
            )
          }
        />
        <LabelValue
          label={trainers.length > 1 ? content.experten : content.experte}
          value={trainers.length > 0 ? trainers.join(', ') : '-'}
        />
        <LabelValue
          label={content.akaVeranstaltungId}
          value={
            <Box sx={{ '& button': { py: 0 }, display: 'flex', alignItems: 'center' }}>
              <Typography variant="labelLight">{veranstaltung.akaVeranstaltungId}</Typography>
              <CopyButton text={veranstaltung.akaVeranstaltungId} />
            </Box>
          }
        />
        <LabelValue label={content.wo} value={printWithNoInfo(printWo(veranstaltung))} />
        <LabelValue label={content.terminart} value={printTerminart(veranstaltung.terminart)} />
        <LabelValue
          label={content.geschaeftsbereich}
          value={
            veranstaltung.geschaeftsbereich === geschaeftsbereiche.UNKNOWN ? undefined : veranstaltung.geschaeftsbereich
          }
        />
        <LabelValue
          label={content.manuellerAusschreibungsprozess}
          value={veranstaltung.manuellerAusschreibungsprozess ? 'Ja' : 'Nein'}
        />
      </Box>
    </Box>
  );
};
